import React, { useState } from 'react';
import { Mail, Phone, MapPin, Linkedin, Instagram, Send, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setStatusMessage('Please fill in all fields');
      setTimeout(() => setSubmitStatus(null), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setStatusMessage('Thank you! Your message has been sent successfully. We will get back to you soon.');
        setFormData({ name: '', email: '', message: '' }); // Clear form
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage('Failed to send message. Please try again or contact us directly.');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'nomoisture@gmail.com',
      link: 'mailto:nomoisture@gmail.com',
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 77100 34766',
      link: 'tel:+917710034766',
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'At Diksal, Post Koshane, Taluka Karjat, District Raigad, Karjat-Kalyan road, near Bhivpuri road station, Diksal, Raigad, Maharashtra, 410201',
      link: null,
    },
  ];

  const socialLinks = [
    {
      icon: Linkedin,
      label: 'LinkedIn',
      link: 'https://linkedin.com/company/nomoisture',
    },
    {
      icon: Instagram,
      label: 'Instagram',
      link: 'https://instagram.com/',
    },
  ];

  return (
    <section 
      id="contact"
      style={{
        background: '#0a0e1a',
        padding: 'clamp(3rem, 8vw, 5rem) 1.5rem',
      }}
    >
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '4rem',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(236, 236, 117, 0.15)',
            border: '1px solid rgba(236, 236, 117, 0.3)',
            padding: '0.5rem 1.5rem',
            borderRadius: '50px',
            marginBottom: '1.5rem',
          }}>
            <Send size={20} style={{ color: '#ECEC75' }} />
            <span style={{ 
              fontSize: '0.875rem', 
              fontWeight: 600,
              color: '#ECEC75',
            }}>
              Get in Touch
            </span>
          </div>

          <h2 style={{
            fontFamily: "'Crimson Text', serif",
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#f1f5f9',
            marginBottom: '1rem',
          }}>
            Contact NOMOISTURE for Container Desiccant Quotes
          </h2>

          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.125rem)',
            color: '#cbd5e1',
            maxWidth: '720px',
            margin: '0 auto',
            lineHeight: 1.8,
          }}>
            Ready to prevent container rain and protect your export cargo from moisture damage?
            Request a B2B quote for NOMOISTURE calcium chloride desiccant bags, silica gel pouches
            and bulk export packaging - our team responds within one business day.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '3rem',
        }} className="contact-grid">
          {/* Contact Information */}
          <div>
            <h3 style={{
              fontFamily: "'Crimson Text', serif",
              fontSize: '1.75rem',
              fontWeight: 700,
              color: '#f1f5f9',
              marginBottom: '2rem',
            }}>
              Contact Information
            </h3>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
            }}>
              {contactInfo.map((info, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: '1.5rem',
                    background: '#121827',
                    border: '1px solid rgba(236, 236, 117, 0.1)',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                  }}
                  className="contact-item"
                >
                  <div style={{
                    background: 'rgba(236, 236, 117, 0.15)',
                    border: '1px solid rgba(236, 236, 117, 0.3)',
                    color: '#ECEC75',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <info.icon size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: '#f1f5f9',
                      marginBottom: '0.25rem',
                    }}>
                      {info.label}
                    </h4>
                    {info.link ? (
                      <a 
                        href={info.link}
                        style={{
                          fontSize: '1rem',
                          color: '#cbd5e1',
                          textDecoration: 'none',
                          transition: 'color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#ECEC75';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#cbd5e1';
                        }}
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p style={{
                        fontSize: '1rem',
                        color: '#cbd5e1',
                        margin: 0,
                      }}>
                        {info.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div style={{
              marginTop: '2rem',
            }}>
              <h4 style={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: '#f1f5f9',
                marginBottom: '1rem',
              }}>
                Follow Us
              </h4>
              <div style={{
                display: 'flex',
                gap: '1rem',
              }}>
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: 'rgba(236, 236, 117, 0.15)',
                      border: '1px solid rgba(236, 236, 117, 0.3)',
                      color: '#ECEC75',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(236, 236, 117, 0.3)';
                      e.currentTarget.style.background = 'rgba(236, 236, 117, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.background = 'rgba(236, 236, 117, 0.15)';
                    }}
                    title={social.label}
                  >
                    <social.icon size={24} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div style={{
            background: '#121827',
            border: '1px solid rgba(236, 236, 117, 0.1)',
            padding: '2rem',
            borderRadius: '12px',
          }}>
            <h3 style={{
              fontFamily: "'Crimson Text', serif",
              fontSize: '1.75rem',
              fontWeight: 700,
              color: '#f1f5f9',
              marginBottom: '1.5rem',
            }}>
              Send us a Message
            </h3>

            <form 
              onSubmit={handleSubmit}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
              }}
            >
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#f1f5f9',
                  marginBottom: '0.5rem',
                }}>
                  Name
                </label>
                <input 
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#0a0e1a',
                    border: '2px solid rgba(236, 236, 117, 0.2)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    color: '#f1f5f9',
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ECEC75';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(236, 236, 117, 0.2)';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#f1f5f9',
                  marginBottom: '0.5rem',
                }}>
                  Email
                </label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#0a0e1a',
                    border: '2px solid rgba(236, 236, 117, 0.2)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    color: '#f1f5f9',
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ECEC75';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(236, 236, 117, 0.2)';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#f1f5f9',
                  marginBottom: '0.5rem',
                }}>
                  Message
                </label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Tell us about your requirements..."
                  required
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#0a0e1a',
                    border: '2px solid rgba(236, 236, 117, 0.2)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    color: '#f1f5f9',
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                    resize: 'vertical',
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ECEC75';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(236, 236, 117, 0.2)';
                  }}
                ></textarea>
              </div>

              {/* Status Messages */}
              {submitStatus && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  borderRadius: '8px',
                  background: submitStatus === 'success' 
                    ? 'rgba(34, 197, 94, 0.15)' 
                    : 'rgba(239, 68, 68, 0.15)',
                  border: `1px solid ${submitStatus === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                }}>
                  {submitStatus === 'success' ? (
                    <CheckCircle size={20} style={{ color: '#22c55e', flexShrink: 0 }} />
                  ) : (
                    <AlertCircle size={20} style={{ color: '#ef4444', flexShrink: 0 }} />
                  )}
                  <p style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    color: submitStatus === 'success' ? '#22c55e' : '#ef4444',
                  }}>
                    {statusMessage}
                  </p>
                </div>
              )}

              <button 
                type="submit"
                disabled={isSubmitting}
                className="btn-primary"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  opacity: isSubmitting ? 0.6 : 1,
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                <Send size={20} />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        .contact-item:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.4);
          border-color: rgba(236, 236, 117, 0.3);
        }
      `}</style>
    </section>
  );
};

export default Contact;
